import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-8 lg:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <Link to="/" className="inline-block">
                <img 
                  src="/images/jaranets-logo.png" 
                  alt="Jara Safety Nets" 
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed">
                Leading safety nets solutions provider serving Andhra Pradesh and Telangana. Based in Karmanghat, Hyderabad, serving Krishna, Guntur, Visakhapatnam, and surrounding areas.
                Professional safety net installation with certified materials and comprehensive safety solutions.
              </p>
            </div>

            {/* Quick Links and Services - Side by side on mobile */}
            <div className="grid grid-cols-2 gap-6 md:col-span-1 lg:col-span-2">
              {/* Quick Links */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Quick Links</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/" className="text-gray-300 hover:text-yellow-500 transition-colors">Home</Link></li>
                  <li><Link to="/products" className="text-gray-300 hover:text-yellow-500 transition-colors">Products</Link></li>
                  <li><Link to="/about" className="text-gray-300 hover:text-yellow-500 transition-colors">About</Link></li>
                  <li><Link to="/blog" className="text-gray-300 hover:text-yellow-500 transition-colors">Blog</Link></li>
                  <li><Link to="/sitemap" className="text-gray-300 hover:text-yellow-500 transition-colors">Sitemap</Link></li>
                </ul>
              </div>

              {/* Services */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Products</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/invisible-grill-balconies" className="text-gray-300 hover:text-yellow-500 transition-colors">Balcony Grills</Link></li>
                  <li><Link to="/invisible-grill-windows" className="text-gray-300 hover:text-yellow-500 transition-colors">Window Grills</Link></li>
                  <li><Link to="/invisible-grill-apartments" className="text-gray-300 hover:text-yellow-500 transition-colors">Apartment Grills</Link></li>
                  <li><Link to="/pigeon-nets" className="text-gray-300 hover:text-yellow-500 transition-colors">Anti Bird Net</Link></li>
                  <li><Link to="/sports-safety" className="text-gray-300 hover:text-yellow-500 transition-colors">Cricket Practice Net</Link></li>
                  <li><Link to="/child-safety-net" className="text-gray-300 hover:text-yellow-500 transition-colors">Children Safety Nets</Link></li>
                  <li><Link to="/pull-dry-cloth-hangers" className="text-gray-300 hover:text-yellow-500 transition-colors">Drying Cloth Hangers</Link></li>
                  <li><Link to="/shade-net" className="text-gray-300 hover:text-yellow-500 transition-colors">Shade Net</Link></li>
                </ul>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Contact Info</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start space-x-3">
                  <Phone className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <a href="tel:+918466055207" className="text-gray-300 hover:text-yellow-500 transition-colors">+91 8466 055 207</a>
                    <br />
                    <a href="tel:+918074616049" className="text-gray-300 hover:text-yellow-500 transition-colors">+91 8074 616 049</a>
                    <p className="text-gray-400 text-xs mt-1">24/7 Support</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">Jarasafetynets@gmail.com</p>
                    <p className="text-gray-400 text-xs">Email Support</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <a 
                      href="https://maps.google.com/maps?q=16.5062,80.6480&z=17&hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-yellow-500 transition-colors cursor-pointer block"
                    >
                      <p>Karmanghat</p>
                      <p>Hyderabad</p>
                      <p>Telangana - 500001</p>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-4 lg:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                © 2025 Jara Safety Nets. All rights reserved.
                <br />
                <span className="text-sm text-gray-400">Designed and developed by <a href="https://www.octaleads.com/" target="_blank" rel="noopener noreferrer" className="text-yellow-500 hover:text-yellow-400 transition-colors">octaleads Pvt Ltd.</a></span>
              </p>
            </div>
            <div className="flex space-x-6 text-sm text-gray-400">
              <Link to="/sitemap" className="hover:text-yellow-500 transition-colors">Sitemap</Link>
              <Link to="/privacy-policy" className="hover:text-yellow-500 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-yellow-500 transition-colors">Terms of Service</Link>
              <Link to="/contact" className="hover:text-yellow-500 transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
